'use client';

import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import styles from './index.module.scss';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';
import EcoImg from 'eco-design/base/eco-img';

// 注册 GSAP 插件
gsap.registerPlugin(CustomEase);

export default function Banner() {
    const { t } = useLang('Banner');
    const { locale } = getLocal();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [leavingIndex, setLeavingIndex] = useState<number | null>(null);
    const [isReady, setIsReady] = useState(false);
    const isAnimating = useRef(false);
    const bgRefs = useRef<(HTMLDivElement | null)[]>([]);
    const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
    const textRefs = useRef<(HTMLDivElement | null)[]>([]);
    const customEasesCreated = useRef(false);

    const sliders = [
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1768470083380-vs42ok-compressed-home-carousel-banner01.jpg',
            h5: 'https://websiteoss.ecoflow.com/figma-uploads/1768470132348-wfsp4g-compressed-home-carousel-banner.jpg',
            label: '&nbsp;',
            title: 'POWER FOR ALL',
            desc: 'Through Power For All, we bring clean, reliable energy to communities and organizations in need. \nTurning Innovation into Impact',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1768470095969-3098m6-compressed-home-carousel-banner02.jpg',
            h5: 'https://websiteoss.ecoflow.com/figma-uploads/1768470144379-qhdz89-compressed-home-carousel-banner02.jpg',
            label: 'POWER FOR',
            title: 'RESCUE',
            desc: 'Through Power For All, we bring clean, reliable energy to communities and organizations in need. \nTurning Innovation into Impact',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1768470107004-mp4ou9-compressed-home-carousel-banner03.jpg',
            h5: 'https://websiteoss.ecoflow.com/figma-uploads/1768470155297-2gg9b1-compressed-home-carousel-banner03.jpg',
            label: 'POWER FOR',
            title: 'ENVIRONMENT',
            desc: 'Through Power For All, we bring clean, reliable energy to communities and organizations in need. \nTurning Innovation into Impact',
        },
        {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1768470117383-p89vmq-compressed-home-carousel-banner04.jpg',
            h5: 'https://websiteoss.ecoflow.com/figma-uploads/1768470167756-vkr95b-compressed-home-carousel-banner04.jpg',
            label: 'POWER FOR',
            title: 'LIVING',
            desc: 'Through Power For All, we bring clean, reliable energy to communities and organizations in need. \nTurning Innovation into Impact',
        },
    ];

    const performTransition = (prevIndex: number, nextIndex: number) => {
        if (isAnimating.current) return;
        isAnimating.current = true;

        // 设置离场状态
        setLeavingIndex(prevIndex);

        const currentSlide = bgRefs.current[prevIndex];
        const currentImage = imageRefs.current[prevIndex];
        const nextSlide = bgRefs.current[nextIndex];
        const nextImage = imageRefs.current[nextIndex];

        if (!currentSlide || !nextSlide || !currentImage || !nextImage) return;

        const SLIDE_DURATION = 1.5;

        // 创建动画时间线
        const tl = gsap.timeline({
            defaults: { duration: SLIDE_DURATION, ease: 'power2.inOut' },
            onComplete: () => {
                // 重置当前 slide
                gsap.set(currentSlide, { visibility: 'hidden' });
                currentSlide.classList.remove('active');
                nextSlide.classList.add('active');
                isAnimating.current = false;
                // 清除离场状态
                setLeavingIndex(null);
            },
        });

        // 设置下一个 slide 的初始状态
        gsap.set(nextSlide, {
            visibility: 'visible',
            scale: 0.7, // 从小开始
            opacity: 0,
            transformOrigin: 'center center',
        });

        // 设置下一个图片的初始状态（反向缩放）
        gsap.set(nextImage, {
            scale: 1.3, // 从大开始
            opacity: 0.5,
            transformOrigin: 'center center',
        });

        // 1. 当前 slide zoom out (0.2s 开始)
        tl.to(
            currentSlide,
            {
                scale: 1.3,
                opacity: 0,
                ease: 'zoomOut',
                duration: SLIDE_DURATION,
            },
            0.2,
        );

        // 2. 当前图片反向缩放 (0.2s 开始)
        tl.to(
            currentImage,
            {
                scale: 0.8,
                ease: 'zoomOut',
                duration: SLIDE_DURATION,
            },
            0.2,
        );

        // 3. 下一个 slide zoom in with bounce (0.4s 开始)
        tl.to(
            nextSlide,
            {
                scale: 1,
                opacity: 1,
                ease: 'bounceOut',
                duration: SLIDE_DURATION,
            },
            0.4,
        );

        // 4. 下一个图片反向缩放到正常 (0.4s 开始)
        tl.to(
            nextImage,
            {
                scale: 1,
                opacity: 1,
                ease: 'zoomIn',
                duration: SLIDE_DURATION,
            },
            0.4,
        );
    };

    useEffect(() => {
        // 创建自定义缓动函数（只创建一次）
        if (!customEasesCreated.current) {
            CustomEase.create('textReveal', '0.77, 0, 0.175, 1');
            CustomEase.create('counterSlide', '0.25, 1, 0.5, 1');
            CustomEase.create('zoomIn', '0.16, 1, 0.3, 1');
            CustomEase.create('zoomOut', '0.7, 0, 0.3, 1');
            CustomEase.create('bounceOut', '0.22, 1.2, 0.36, 1');
            customEasesCreated.current = true;
        }

        // 延迟触发首次入场动画
        requestAnimationFrame(() => {
            setIsReady(true);
        });

        // 自动轮播
        const interval = setInterval(() => {
            setCurrentIndex((prev) => {
                const prevIndex = prev;
                const nextIndex = (prev + 1) % sliders.length;
                // setLeavingIndex(prevIndex);
                performTransition(prevIndex, nextIndex);
                return nextIndex;
            });
        }, 5000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliders.length]);

    return (
        <div className={`${styles.Banner} GtmModuleData-section`}>
            {/* 背景图片层 */}
            <div className="background-layer">
                {sliders.map((slide, index) => (
                    <div key={`bg-${index}`} ref={(el) => (bgRefs.current[index] = el)} className={`bg-image ${index === 0 ? 'active' : ''}`}>
                        <div className="image-inner" ref={(el) => (imageRefs.current[index] = el)}>
                            <EcoImg pc={slide.pc} pad={slide.h5} h5={slide.h5} />
                        </div>
                    </div>
                ))}
            </div>

            {/* 文字层 */}
            <div className="text-layer">
                {sliders.map((slide, index) => (
                    <div key={`text-${index}`} className={`text-group ${index === currentIndex ? 'active' : ''} ${index === leavingIndex ? 'leaving' : ''}`}>
                        <div className="text-wrapper">
                            {/* 渲染&nbsp; */}
                            {slide.label === '&nbsp;' && <div className="label slider">&nbsp;</div>}
                            {slide.label !== '&nbsp;' && <div className="label slider">{slide.label}</div>}
                        </div>
                        <div className="text-wrapper">
                            <h1 className="title slider">{slide.title}</h1>
                        </div>
                        <div className="text-wrapper">
                            <p className="desc slider">{slide.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
